import * as echarts from 'echarts'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'

export type SmoothLineChartData = {
  [key: string]: string
}

type SmoothLineChartProps = {
  title?: string
  data: SmoothLineChartData[] | undefined
  height?: number | string
  width?: number | string
}

const toCssSize = (size?: number | string) =>
  typeof size === 'number' ? `${size}px` : size || '100%'

const SmoothLineChartWrapper = styled.div<Partial<SmoothLineChartProps>>`
  width: ${(props) => toCssSize(props.width)};
  height: ${(props) => toCssSize(props.height)};
  min-height: 0;
`

export const SMPLineChart = <T extends SmoothLineChartProps>(
  smoothLineChartProps: T
): React.ReactElement => {
  const chartDom = useRef<HTMLDivElement>(null)
  const { title, data, height = '100%', width = '100%' } = smoothLineChartProps

  const seriesValue = data?.map((item) => [item.date, item.value])
  const seriesMaxSmp = data?.map((item) => [item.date, item.maxSmp])
  const seriesMinSmp = data?.map((item) => [item.date, item.minSmp])
  const seriesWeightedAver = data?.map((item) => [item.date, item.weightedAver])

  useEffect(() => {
    if (chartDom.current) {
      const chartInstance = echarts.init(chartDom.current)
      chartInstance.setOption({
        tooltip: {
          trigger: 'axis',
        },
        xAxis: {
          type: 'time',
        },
        yAxis: {
          type: 'value',
        },
        series: [
          {
            name: '실제 데이터',
            data: seriesValue,
            type: 'line',
            smooth: true,
          },
          {
            name: '최대',
            data: seriesMaxSmp,
            type: 'line',
            smooth: true,
          },
          {
            name: '최소',
            data: seriesMinSmp,
            type: 'line',
            smooth: true,
          },
          {
            name: '가중평균',
            data: seriesWeightedAver,
            type: 'line',
            smooth: true,
          },
        ],
      })

      const resizeObserver = new ResizeObserver(() => {
        chartInstance.resize()
      })
      resizeObserver.observe(chartDom.current)

      return () => {
        resizeObserver.disconnect()
        chartInstance.dispose()
      }
    }
  }, [title, seriesValue, seriesMaxSmp, seriesMinSmp, seriesWeightedAver])
  return (
    <SmoothLineChartWrapper width={width} height={height}>
      <div
        id="smooth-line-chart"
        ref={chartDom}
        style={{ width: width, height: height }}
      />
    </SmoothLineChartWrapper>
  )
}
